/**
 * revealIntensity — how loud the "card added" moment should be.
 *
 * Rarity is currently only a *label* on a tile. Scaling the celebration by it
 * makes rarity *felt*: a Common slots in quietly, a Special Illustration Rare
 * stops the screen. That contrast is the whole point — if every add were a
 * spectacle, none of them would be.
 *
 * Pure function, no rendering, so the ladder is testable and tunable without
 * touching the component.
 */
import { getRarityBadge } from "../cards/cardDisplay";

/**
 * Tiers, quietest first.
 *   quiet   — no overlay at all; the tile handles it. Commons/Uncommons.
 *   notable — brief inline flourish. Rares, Holos.
 *   chase   — full-screen moment with foil burst. The cards people hunt.
 */
export const REVEAL_TIERS = {
  quiet: {
    tier: "quiet",
    holdMs: 900,
    particleCount: 0,
    particleVariant: "sparkle",
    showOverlay: false,
    glow: false,
  },
  notable: {
    tier: "notable",
    holdMs: 1600,
    particleCount: 14,
    particleVariant: "sparkle",
    showOverlay: true,
    glow: true,
  },
  chase: {
    tier: "chase",
    holdMs: 2600,
    particleCount: 30,
    particleVariant: "foil",
    showOverlay: true,
    glow: true,
  },
};

/**
 * Rarity labels that earn the full moment. These are the cards a collector
 * actually chases — matched against the *normalised* label from getRarityBadge,
 * so API word-order quirks ("Rare Secret" vs "Secret Rare") are already handled.
 */
const CHASE_LABELS = new Set([
  "Special Illustration Rare",
  "Illustration Rare",
  "Mega Hyper Rare",
  "Hyper Rare",
  "ACE SPEC Rare",
  "Shiny Ultra Rare",
  "Secret Rare",
  "Rainbow Rare",
  "Ultra Rare",
  "Amazing Rare",
  "Radiant Rare",
  "Double Rare",
  "Triple Rare",
  "Full Art",
  "Legendary",
]);

const QUIET_LABELS = new Set(["Common", "Uncommon", "Promo"]);

/**
 * Tier for a card.
 *
 * Unknown rarities land in `notable` rather than `quiet`: a rarity we don't
 * recognise is more likely to be a new chase tier than a new bulk tier, and
 * under-celebrating a grail is the worse failure.
 *
 * @returns {{tier, holdMs, particleCount, particleVariant, showOverlay, glow, label}}
 */
export const getRevealIntensity = (card) => {
  const badge = getRarityBadge(card);

  if (!badge) return { ...REVEAL_TIERS.quiet, label: null };

  const label = badge.label;
  if (CHASE_LABELS.has(label)) return { ...REVEAL_TIERS.chase, label };
  if (QUIET_LABELS.has(label)) return { ...REVEAL_TIERS.quiet, label };
  if (label === "Rare" || label === "Holo Rare" || label === "Shiny Rare") {
    return { ...REVEAL_TIERS.notable, label };
  }
  return { ...REVEAL_TIERS.notable, label };
};
